import { useCallback, useEffect, useRef, useState } from 'react';
import GameAPI from '../GameAPI';
import GameStore from '../../../store/GameStore';
import WebSocketService from '../../../services/WebSocketService';
import ToastService from '../../../services/ToastService';
import {
  normalizeFieldNames,
  reduceReconciliationEvent,
} from '../../../events/UserEventReducer';
import UserEventTypes from '../../../events/UserEventTypes';
import {
  formatCombatLogEntry,
  formatCombatOutcome,
  normalizeCombatEvent,
} from './combatEvents';
import {
  normalizeGroundLootSummary,
  normalizeRegionWorldSnapshot,
  normalizeStarterCacheSummary,
} from '../GroundLoot/groundLoot';
import { normalizeBaseCamp } from '../BaseCamp/baseCamp';
import { normalizeClientBootstrap } from './clientBootstrap';

const MAX_SEEN_EVENT_IDS = 500;
const MAX_BUFFERED_INVENTORY_EVENTS = 500;
const MAX_BUFFERED_USER_EVENTS = 500;
export const MAX_RETAINED_MESSAGES = 500;

export function retainLatestMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.length > MAX_RETAINED_MESSAGES
    ? messages.slice(-MAX_RETAINED_MESSAGES)
    : messages;
}

function eventCursor(source) {
  const timestamp = source?.latestAppliedEventAt
    ?? source?.LatestAppliedEventAt
    ?? source?.CreatedAt
    ?? source?.createdAt;
  const id = source?.latestAppliedEventId
    ?? source?.LatestAppliedEventId
    ?? source?.Id
    ?? source?.id;
  const timestampMs = Date.parse(timestamp);
  if (!Number.isFinite(timestampMs)) return null;
  return {
    timestamp: new Date(timestampMs).toISOString(),
    timestampMs,
    id: String(id || ''),
  };
}

function compareEventCursors(left, right) {
  if (!left && !right) return 0;
  if (!left) return -1;
  if (!right) return 1;
  if (left.timestampMs !== right.timestampMs) {
    return left.timestampMs - right.timestampMs;
  }
  return left.id.localeCompare(right.id);
}

function isInventoryEvent(event) {
  const type = event?.EventType ?? event?.eventType;
  return type === 'InventoryAddItem'
    || type === 'InventoryRemoveItem'
    || type === UserEventTypes.CONSUME_ITEM
    || type === UserEventTypes.QUICK_BELT_CHANGED
    || type === UserEventTypes.BASE_CAMP_CHANGED
    || type === UserEventTypes.RESOURCE_COLLECTION_COMPLETED;
}

export { normalizeGroundLootSummary, normalizeRegionWorldSnapshot };

export function createRegionPlaceholder(regionId) {
  const id = String(regionId || '').trim();
  return id ? { id } : null;
}

function sameOnlinePlayers(left, right) {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
  return left.every((player, index) => {
    const next = right[index];
    return player?.name === next?.name
      && player?.x === next?.x
      && player?.y === next?.y
      && player?.currentAction === next?.currentAction
      && player?.tilePopulation === next?.tilePopulation
      && JSON.stringify(player?.avatarSelection || null)
        === JSON.stringify(next?.avatarSelection || null);
  });
}

export default function useGameConnection(
  onCombatEvent,
  onCombatRewardEvent,
) {
  const [user, setUser] = useState(null);
  const [region, setRegion] = useState(null);
  const [regionWorldState, setRegionWorldState] = useState(() => (
    normalizeRegionWorldSnapshot([], null)
  ));
  const [starterCache, setStarterCache] = useState(null);
  const [baseCamp, setBaseCamp] = useState(null);
  const [baseCampLoading, setBaseCampLoading] = useState(true);
  const [baseCampError, setBaseCampError] = useState('');
  const [resourceNodesLoading, setResourceNodesLoading] = useState(false);
  const [resourceNodesError, setResourceNodesError] = useState('');
  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState({
    status: 'loading',
    error: null,
  });
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const unsubscribeStoreRef = useRef(null);
  const unsubscribeWsRef = useRef(null);
  const unsubscribeWsConnectionRef = useRef(null);
  const seenEventIdsRef = useRef(new Set());
  const seenEventOrderRef = useRef([]);
  const inventoryEventsRef = useRef([]);
  const userEventsRef = useRef([]);
  const regionIdRef = useRef(null);
  const regionWorldEpochRef = useRef(0);
  const groundLootRequestSequenceRef = useRef(0);
  const resourceNodeRequestSequenceRef = useRef(0);
  const acceptedWorldSocketSequenceRef = useRef(0);
  const latestWorldGeneratedAtRef = useRef(0);
  const serverTimeOffsetMsRef = useRef(0);
  const worldInstanceIdRef = useRef('shared');
  const regionResyncSequenceRef = useRef(0);
  const reconnectResyncRequiredRef = useRef(false);
  const reconnectSnapshotRef = useRef(null);
  const reconnectRetryTimerRef = useRef(null);
  const lastLoggedActionRef = useRef(null);
  const activeCombatRewardIdRef = useRef(null);
  const baseCampSnapshotRef = useRef(null);
  const baseCampApplyGenerationRef = useRef(0);

  const appendMessage = useCallback((messageFactory) => {
    setMessages(prev => {
      const message = typeof messageFactory === 'function' ? messageFactory(prev) : messageFactory;
      if (Array.isArray(message)) return retainLatestMessages(message);
      if (message === null || message === undefined) return prev;
      return retainLatestMessages([...prev, message]);
    });
  }, []);

  const retryConnection = useCallback(() => {
    setBootstrapAttempt(current => current + 1);
  }, []);

  const refreshGroundLoot = useCallback(async () => {
    const requestedRegionId = regionIdRef.current;
    const requestedWorldInstanceId = worldInstanceIdRef.current;
    const requestedEpoch = regionWorldEpochRef.current;
    const socketSequenceAtStart = acceptedWorldSocketSequenceRef.current;
    groundLootRequestSequenceRef.current += 1;
    const requestSequence = groundLootRequestSequenceRef.current;
    const payload = await GameAPI.fetchGroundLoot();
    if (
      requestedEpoch !== regionWorldEpochRef.current
      || requestSequence !== groundLootRequestSequenceRef.current
      || socketSequenceAtStart !== acceptedWorldSocketSequenceRef.current
      || String(requestedRegionId || '') !== String(regionIdRef.current || '')
      || String(requestedWorldInstanceId || '')
        !== String(worldInstanceIdRef.current || '')
    ) {
      return null;
    }
    const next = normalizeRegionWorldSnapshot(
      payload,
      requestedRegionId,
      serverTimeOffsetMsRef.current,
      requestedWorldInstanceId,
    );
    const hasServerTimestamp = (
      payload?.generatedAtMs != null
      || payload?.GeneratedAtMs != null
    );
    if (
      regionIdRef.current
      && next.regionId
      && String(next.regionId) !== String(regionIdRef.current)
    ) {
      return null;
    }
    if (
      next.worldInstanceId
      && String(next.worldInstanceId)
        !== String(worldInstanceIdRef.current || '')
    ) {
      return null;
    }
    if (
      hasServerTimestamp
      && next.generatedAtMs < latestWorldGeneratedAtRef.current
    ) {
      return null;
    }
    if (hasServerTimestamp) {
      latestWorldGeneratedAtRef.current = next.generatedAtMs;
      serverTimeOffsetMsRef.current = next.serverTimeOffsetMs;
    }
    setRegionWorldState(current => ({
      ...next,
      // The ground-loot endpoint serializes an empty ResourceNodes list.
      // Nodes and presence are owned by their refresh or the combined WS
      // snapshot.
      resourceNodes: (
        String(current?.regionId || '') === String(next.regionId || '')
        && String(current?.worldInstanceId || '')
          === String(next.worldInstanceId || '')
          ? current.resourceNodes || []
          : []
      ),
      onlinePlayers: (
        String(current?.regionId || '') === String(next.regionId || '')
        && String(current?.worldInstanceId || '')
          === String(next.worldInstanceId || '')
          ? current.onlinePlayers || []
          : []
      ),
    }));
    return next;
  }, []);

  const refreshResourceNodes = useCallback(async () => {
    const requestedRegionId = regionIdRef.current;
    const requestedWorldInstanceId = worldInstanceIdRef.current;
    const requestedEpoch = regionWorldEpochRef.current;
    const socketSequenceAtStart = acceptedWorldSocketSequenceRef.current;
    resourceNodeRequestSequenceRef.current += 1;
    const requestSequence = resourceNodeRequestSequenceRef.current;
    const isSameRequestScope = () => (
      requestedEpoch === regionWorldEpochRef.current
      && requestSequence === resourceNodeRequestSequenceRef.current
      && String(requestedRegionId || '') === String(regionIdRef.current || '')
      && String(requestedWorldInstanceId || '')
        === String(worldInstanceIdRef.current || '')
    );
    const canApplyResponse = () => (
      isSameRequestScope()
      && socketSequenceAtStart === acceptedWorldSocketSequenceRef.current
    );

    setResourceNodesLoading(true);
    setResourceNodesError('');
    try {
      const payload = await GameAPI.fetchGatheringNodes();
      if (!canApplyResponse()) return null;

      const next = normalizeRegionWorldSnapshot(
        payload,
        requestedRegionId,
        serverTimeOffsetMsRef.current,
        requestedWorldInstanceId,
      );
      if (
        String(next.regionId || '') !== String(requestedRegionId || '')
        || String(next.worldInstanceId || '')
          !== String(requestedWorldInstanceId || '')
      ) {
        return null;
      }
      setRegionWorldState(current => ({
        ...current,
        messageType: next.messageType,
        regionId: next.regionId,
        worldInstanceId: next.worldInstanceId,
        resourceNodes: next.resourceNodes,
        champion: next.champion,
        groundLoot: (
          String(current?.regionId || '') === String(next.regionId || '')
          && String(current?.worldInstanceId || '')
            === String(next.worldInstanceId || '')
        ) ? current.groundLoot || [] : [],
        worldLootContainers: (
          String(current?.regionId || '') === String(next.regionId || '')
          && String(current?.worldInstanceId || '')
            === String(next.worldInstanceId || '')
        ) ? current.worldLootContainers || [] : [],
      }));
      return next;
    } catch (error) {
      if (canApplyResponse()) {
        const detail = error instanceof Error && error.message
          ? ` ${error.message}`
          : '';
        setResourceNodesError(
          `Resource nodes could not be loaded.${detail}`,
        );
      }
      throw error;
    } finally {
      if (isSameRequestScope()) {
        setResourceNodesLoading(false);
      }
    }
  }, []);

  const refreshStarterCache = useCallback(async () => {
    const payload = await GameAPI.fetchStarterCache();
    const next = normalizeStarterCacheSummary(payload);
    setStarterCache(next);
    return next;
  }, []);

  const applyBaseCampSnapshot = useCallback((snapshot, requestGeneration = null) => {
    const next = normalizeBaseCamp(snapshot);
    if (!next) return null;
    const current = baseCampSnapshotRef.current;
    const nextTimestamp = next.generatedAtMs;
    const currentTimestamp = current?.generatedAtMs;
    if (
      nextTimestamp != null
      && currentTimestamp != null
      && nextTimestamp < currentTimestamp
    ) return null;
    if (
      nextTimestamp == null
      && requestGeneration !== null
      && requestGeneration !== baseCampApplyGenerationRef.current
    ) return null;
    baseCampSnapshotRef.current = next;
    baseCampApplyGenerationRef.current += 1;
    setBaseCamp(next);
    setBaseCampError('');
    return next;
  }, []);

  const reconcileBaseCampSnapshot = useCallback(snapshot => (
    applyBaseCampSnapshot(snapshot)
  ), [applyBaseCampSnapshot]);

  const refreshBaseCamp = useCallback(async () => {
    const requestGeneration = baseCampApplyGenerationRef.current;
    setBaseCampLoading(true);
    setBaseCampError('');
    try {
      const payload = await GameAPI.fetchBaseCamp();
      return applyBaseCampSnapshot(payload, requestGeneration);
    } catch (error) {
      setBaseCampError(
        error instanceof Error
          ? error.message
          : 'Camp projects could not be loaded.',
      );
      throw error;
    } finally {
      setBaseCampLoading(false);
    }
  }, [applyBaseCampSnapshot]);

  const reconcileInventory = useCallback((
    inventory,
    _operationId = null,
    snapshot = null,
  ) => {
    if (!Array.isArray(inventory)) return;
    const currentUser = GameStore.getState().user;
    if (!currentUser) return;
    const snapshotCursor = eventCursor(snapshot);
    const currentCursor = eventCursor(currentUser);
    let appliedCursor = compareEventCursors(snapshotCursor, currentCursor) > 0
      ? snapshotCursor
      : currentCursor;
    let reconciledUser = { ...currentUser, inventory };
    if (snapshotCursor) {
      inventoryEventsRef.current
        .filter(event => (
          compareEventCursors(eventCursor(event), snapshotCursor) > 0
        ))
        .sort((left, right) => (
          compareEventCursors(eventCursor(left), eventCursor(right))
        ))
        .forEach(event => {
          reconciledUser = reduceReconciliationEvent(reconciledUser, event);
          const replayedCursor = eventCursor(event);
          if (compareEventCursors(replayedCursor, appliedCursor) > 0) {
            appliedCursor = replayedCursor;
          }
        });
    }
    if (appliedCursor) {
      reconciledUser.latestAppliedEventAt = appliedCursor.timestamp;
      reconciledUser.latestAppliedEventId = appliedCursor.id;
    }
    GameStore.hydrate(reconciledUser);
  }, []);

  const reconcileUserSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return null;
    const currentUser = GameStore.getState().user;
    const userSnapshot = snapshot.user ?? snapshot.User;
    if (!currentUser && !userSnapshot) return null;
    const snapshotCursor = eventCursor(snapshot)
      ?? eventCursor(userSnapshot);
    let appliedCursor = snapshotCursor;
    let reconciledUser = {
      ...(currentUser || {}),
      ...(userSnapshot || {}),
    };
    const inventory = snapshot.inventory
      ?? snapshot.Inventory
      ?? userSnapshot?.inventory
      ?? userSnapshot?.Inventory;
    if (Array.isArray(inventory)) {
      reconciledUser.inventory = inventory;
    }
    const quickBelt = snapshot.quickBelt
      ?? snapshot.QuickBelt
      ?? userSnapshot?.quickBelt
      ?? userSnapshot?.QuickBelt;
    if (Array.isArray(quickBelt)) {
      reconciledUser.quickBelt = normalizeFieldNames(quickBelt);
    }
    const quickBeltCapacity = snapshot.quickBeltCapacity
      ?? snapshot.QuickBeltCapacity
      ?? userSnapshot?.quickBeltCapacity
      ?? userSnapshot?.QuickBeltCapacity;
    if (quickBeltCapacity !== undefined) {
      reconciledUser.quickBeltCapacity = Math.min(
        8,
        Math.max(1, Number(quickBeltCapacity) || 1),
      );
    }
    const knownRecipeIds = snapshot.knownRecipeIds
      ?? snapshot.KnownRecipeIds
      ?? userSnapshot?.knownRecipeIds
      ?? userSnapshot?.KnownRecipeIds;
    if (Array.isArray(knownRecipeIds)) {
      reconciledUser.knownRecipeIds = [...new Set(
        knownRecipeIds.map(recipeId => String(recipeId || '').trim()).filter(Boolean),
      )];
    }
    const currentHp = snapshot.currentHp
      ?? snapshot.CurrentHp
      ?? userSnapshot?.currentHp
      ?? userSnapshot?.CurrentHp;
    if (currentHp !== undefined) {
      reconciledUser.currentHp = Number(currentHp);
    }
    const satiety = snapshot.satiety
      ?? snapshot.Satiety
      ?? userSnapshot?.satiety
      ?? userSnapshot?.Satiety;
    if (satiety !== undefined) {
      reconciledUser.satiety = Number(satiety);
    }
    const hungerState = snapshot.hungerState
      ?? snapshot.HungerState
      ?? userSnapshot?.hungerState
      ?? userSnapshot?.HungerState;
    if (hungerState !== undefined) {
      reconciledUser.hungerState = hungerState;
    }
    const consumableEffectQueue = snapshot.consumableEffectQueue
      ?? snapshot.ConsumableEffectQueue
      ?? userSnapshot?.consumableEffectQueue
      ?? userSnapshot?.ConsumableEffectQueue;
    if (Array.isArray(consumableEffectQueue)) {
      reconciledUser.consumableEffectQueue = normalizeFieldNames(
        consumableEffectQueue,
      );
    }
    const statsCached = snapshot.statsCached
      ?? snapshot.StatsCached
      ?? userSnapshot?.statsCached
      ?? userSnapshot?.StatsCached;
    if (statsCached && typeof statsCached === 'object') {
      reconciledUser.statsCached = normalizeFieldNames(statsCached);
    }
    if (snapshotCursor) {
      inventoryEventsRef.current
        .filter(event => (
          compareEventCursors(eventCursor(event), snapshotCursor) > 0
        ))
        .sort((left, right) => (
          compareEventCursors(eventCursor(left), eventCursor(right))
        ))
        .forEach(event => {
          reconciledUser = reduceReconciliationEvent(reconciledUser, event);
          const replayedCursor = eventCursor(event);
          if (compareEventCursors(replayedCursor, appliedCursor) > 0) {
            appliedCursor = replayedCursor;
          }
        });
    }
    if (appliedCursor) {
      reconciledUser.latestAppliedEventAt = appliedCursor.timestamp;
      reconciledUser.latestAppliedEventId = appliedCursor.id;
    }
    GameStore.hydrate(reconciledUser);
    return reconciledUser;
  }, []);

  const refreshUser = useCallback(async () => {
    const fetchedUser = await GameAPI.fetchUser();
    if (!fetchedUser) return fetchedUser;
    const currentUser = GameStore.getState().user;
    if (currentUser) {
      reconcileUserSnapshot({
        ...fetchedUser,
        user: fetchedUser,
      });
    } else {
      GameStore.hydrate(fetchedUser);
      setUser(fetchedUser);
    }
    return fetchedUser;
  }, [reconcileUserSnapshot]);

  const resyncAuthoritativeRegion = useCallback(async snapshot => {
    regionResyncSequenceRef.current += 1;
    const resyncSequence = regionResyncSequenceRef.current;
    let bootstrap;
    try {
      bootstrap = normalizeClientBootstrap(await GameAPI.fetchBootstrap());
    } catch (error) {
      console.error(
        'Failed to verify the region from a reconnect snapshot',
        error,
      );
      return false;
    }
    if (resyncSequence !== regionResyncSequenceRef.current) {
      return false;
    }

    const {
      user: fetchedUser,
      region: fetchedRegion,
      starterCache: fetchedStarterCache,
      baseCamp: fetchedBaseCamp,
    } = bootstrap;
    const userRegionId = fetchedUser?.currentRegionId
      ?? fetchedUser?.CurrentRegionId;
    const userWorldInstanceId = fetchedUser?.currentWorldInstanceId
      ?? fetchedUser?.CurrentWorldInstanceId
      ?? 'shared';
    const fetchedRegionId = fetchedRegion?.id ?? fetchedRegion?.Id;
    if (
      !snapshot.regionId
      || String(snapshot.regionId) !== String(userRegionId || '')
      || String(snapshot.regionId) !== String(fetchedRegionId || '')
      || String(snapshot.worldInstanceId || '')
        !== String(userWorldInstanceId)
    ) {
      return false;
    }

    regionIdRef.current = snapshot.regionId;
    worldInstanceIdRef.current = snapshot.worldInstanceId;
    regionWorldEpochRef.current += 1;
    groundLootRequestSequenceRef.current += 1;
    resourceNodeRequestSequenceRef.current += 1;
    acceptedWorldSocketSequenceRef.current += 1;
    latestWorldGeneratedAtRef.current = snapshot.generatedAtMs;
    serverTimeOffsetMsRef.current = snapshot.serverTimeOffsetMs;
    setRegion(fetchedRegion);
    setStarterCache(fetchedStarterCache);
    if (fetchedBaseCamp) reconcileBaseCampSnapshot(fetchedBaseCamp);
    GameStore.hydrate(fetchedUser);
    const fetchedCursor = eventCursor(fetchedUser);
    userEventsRef.current
      .filter(event => (
        compareEventCursors(eventCursor(event), fetchedCursor) > 0
      ))
      .sort((left, right) => (
        compareEventCursors(eventCursor(left), eventCursor(right))
      ))
      .forEach(event => GameStore.dispatch(event));
    setUser(GameStore.getState().user || fetchedUser);
    setRegionWorldState(snapshot);
    setResourceNodesError('');
    setResourceNodesLoading(false);
    return true;
  }, [reconcileBaseCampSnapshot]);

  useEffect(() => {
    let mounted = true;
    const bufferedInventoryEvents = inventoryEventsRef.current;
    const bufferedUserEvents = userEventsRef.current;
    setConnectionState({ status: 'loading', error: null });

    const scheduleReconnectRetry = () => {
      if (reconnectRetryTimerRef.current) return;
      reconnectRetryTimerRef.current = window.setTimeout(async () => {
        reconnectRetryTimerRef.current = null;
        const snapshot = reconnectSnapshotRef.current;
        if (!mounted || !reconnectResyncRequiredRef.current || !snapshot) {
          return;
        }
        if (await resyncAuthoritativeRegion(snapshot)) {
          reconnectResyncRequiredRef.current = false;
          reconnectSnapshotRef.current = null;
          return;
        }
        scheduleReconnectRetry();
      }, 1000);
    };

    async function bootstrap() {
      const payload = normalizeClientBootstrap(await GameAPI.fetchBootstrap());
      if (!mounted) return;
      const fetchedUser = payload.user;
      const fetchedRegion = payload.region;
      setUser(fetchedUser);
      setRegion(fetchedRegion);
      setStarterCache(payload.starterCache);
      if (payload.baseCamp) reconcileBaseCampSnapshot(payload.baseCamp);
      setRegionWorldState(payload.regionWorldState);
      regionIdRef.current = payload.regionWorldState?.regionId
        ?? fetchedRegion?.id
        ?? fetchedRegion?.Id
        ?? null;
      worldInstanceIdRef.current = payload.regionWorldState?.worldInstanceId
        ?? fetchedUser?.currentWorldInstanceId
        ?? fetchedUser?.CurrentWorldInstanceId
        ?? 'shared';
      latestWorldGeneratedAtRef.current = 0;
      serverTimeOffsetMsRef.current = 0;

      GameStore.hydrate(fetchedUser);
      const worldResults = await Promise.allSettled([
        refreshGroundLoot(),
        refreshResourceNodes(),
      ]);
      worldResults
        .filter(result => result.status === 'rejected')
        .forEach(result => console.error(
          'Failed to load shared region state',
          result.reason,
        ));
      if (!payload.baseCamp) {
        try {
          await refreshBaseCamp();
        } catch (error) {
          console.error('Failed to load Camp projects', error);
        }
      }
      if (!mounted) return;

      if (Number(fetchedUser?.idleSimulationPausedAt || 0) > 0) {
        appendMessage(prev => {
          const alreadyLogged = prev.some(msg => msg.startsWith('Idle progression paused'));
          if (alreadyLogged) return prev;
          return [...prev, 'Idle progression paused — automation is waiting for you to resume.'];
        });
      }

      unsubscribeStoreRef.current = GameStore.subscribe((state) => {
        const nextUser = state.user;
        setUser(nextUser);
        const nextRegionId = nextUser?.currentRegionId ?? nextUser?.CurrentRegionId;
        const nextWorldInstanceId = nextUser?.currentWorldInstanceId
          ?? nextUser?.CurrentWorldInstanceId
          ?? 'shared';
        const regionChanged = nextRegionId
          && String(nextRegionId) !== String(regionIdRef.current || '');
        const worldInstanceChanged = String(nextWorldInstanceId)
          !== String(worldInstanceIdRef.current || '');
        if (regionChanged || worldInstanceChanged) {
          regionIdRef.current = nextRegionId;
          worldInstanceIdRef.current = nextWorldInstanceId;
          regionWorldEpochRef.current += 1;
          groundLootRequestSequenceRef.current += 1;
          resourceNodeRequestSequenceRef.current += 1;
          latestWorldGeneratedAtRef.current = 0;
          serverTimeOffsetMsRef.current = 0;
          setRegionWorldState(normalizeRegionWorldSnapshot(
            {
              regionId: nextRegionId,
              worldInstanceId: nextWorldInstanceId,
              groundLoot: [],
              worldLootContainers: [],
              resourceNodes: [],
            },
            nextRegionId,
            0,
            nextWorldInstanceId,
          ));
          if (regionChanged) {
            setRegion(createRegionPlaceholder(nextRegionId));
            GameAPI.fetchCurrentRegion()
              .then(nextRegion => {
                const fetchedRegionId = nextRegion?.id ?? nextRegion?.Id;
                if (
                  mounted
                  && fetchedRegionId
                  && String(fetchedRegionId) === String(regionIdRef.current)
                ) {
                  setRegion(nextRegion);
                }
              })
              .catch(error => console.error('Failed to refresh region metadata', error));
          }
          Promise.allSettled([
            refreshGroundLoot(),
            refreshResourceNodes(),
          ]).then(results => results
            .filter(result => result.status === 'rejected')
            .forEach(result => console.error(
              'Failed to refresh shared state after changing world',
              result.reason,
            )));
        }
      });

      unsubscribeWsRef.current = WebSocketService.subscribe(async (evt) => {
        const messageType = evt?.MessageType ?? evt?.messageType;
        if (messageType === 'RegionWorldSnapshot') {
          const snapshot = normalizeRegionWorldSnapshot(
            evt,
            regionIdRef.current,
            serverTimeOffsetMsRef.current,
            worldInstanceIdRef.current,
          );
          const regionChanged = (
            regionIdRef.current
            && snapshot.regionId
            && String(snapshot.regionId)
              !== String(regionIdRef.current)
          );
          const worldInstanceChanged = (
            snapshot.worldInstanceId
            && String(snapshot.worldInstanceId)
              !== String(worldInstanceIdRef.current || '')
          );
          if (
            reconnectResyncRequiredRef.current
            || regionChanged
            || worldInstanceChanged
          ) {
            if (reconnectResyncRequiredRef.current) {
              reconnectSnapshotRef.current = snapshot;
            }
            if (await resyncAuthoritativeRegion(snapshot)) {
              reconnectResyncRequiredRef.current = false;
              reconnectSnapshotRef.current = null;
            } else if (reconnectResyncRequiredRef.current) {
              scheduleReconnectRetry();
            }
            return;
          }
          if (snapshot.generatedAtMs >= latestWorldGeneratedAtRef.current) {
            latestWorldGeneratedAtRef.current = snapshot.generatedAtMs;
            serverTimeOffsetMsRef.current = snapshot.serverTimeOffsetMs;
            acceptedWorldSocketSequenceRef.current += 1;
            const includesResourceNodes = (
              evt?.resourceNodes != null
              || evt?.ResourceNodes != null
            );
            if (includesResourceNodes) {
              setResourceNodesError('');
              setResourceNodesLoading(false);
            }
            setRegionWorldState(current => ({
              ...snapshot,
              resourceNodes: includesResourceNodes
                ? snapshot.resourceNodes
                : current?.resourceNodes || [],
              onlinePlayers: sameOnlinePlayers(
                current?.onlinePlayers,
                snapshot.onlinePlayers,
              ) ? current.onlinePlayers : snapshot.onlinePlayers,
            }));
          }
          return;
        }
        if (messageType === 'BaseCampSnapshot') {
          reconcileBaseCampSnapshot(evt);
          return;
        }

        const eventId = evt?.Id;
        if (eventId && seenEventIdsRef.current.has(eventId)) return;
        if (eventId) {
          seenEventIdsRef.current.add(eventId);
          seenEventOrderRef.current.push(eventId);
          if (seenEventOrderRef.current.length > MAX_SEEN_EVENT_IDS) {
            const expiredId = seenEventOrderRef.current.shift();
            seenEventIdsRef.current.delete(expiredId);
          }
        }

        const isInventoryTransferEvent = isInventoryEvent(evt);
        userEventsRef.current.push(evt);
        if (userEventsRef.current.length > MAX_BUFFERED_USER_EVENTS) {
          userEventsRef.current.shift();
        }
        const inventoryEventCursor = isInventoryTransferEvent
          ? eventCursor(evt)
          : null;
        if (inventoryEventCursor) {
          inventoryEventsRef.current.push(evt);
          if (
            inventoryEventsRef.current.length
            > MAX_BUFFERED_INVENTORY_EVENTS
          ) {
            inventoryEventsRef.current.shift();
          }
        }
        if (
          inventoryEventCursor
          && compareEventCursors(
            inventoryEventCursor,
            eventCursor(GameStore.getState().user),
          ) <= 0
        ) {
          return;
        }

        const receivedAtMs = Date.now();
        GameStore.dispatch(evt);
        try {
          const eventType = evt.EventType ?? evt.eventType;
          const details = evt.Details ?? evt.details ?? {};
          const combatEvent = normalizeCombatEvent(evt, receivedAtMs);
          if (combatEvent
            && (combatEvent.kind === 'snapshot' || combatEvent.isFresh)
            && typeof onCombatEvent === 'function') {
            onCombatEvent(combatEvent);
          }

          switch (eventType) {
            case 'InventoryAddItem': {
              const combatRewardId = activeCombatRewardIdRef.current;
              const itemObj = await GameAPI.fetchItemAuto(
                details.itemId ?? details.ItemId,
                details.itemInstanceId ?? details.ItemInstanceId,
              );
              appendMessage(
                `Looted ${itemObj?.name || 'item'} x${details.quantity ?? details.Quantity}`,
              );
              if (combatRewardId && typeof onCombatRewardEvent === 'function') {
                onCombatRewardEvent({
                  kind: 'loot',
                  rewardId: combatRewardId,
                  item: itemObj,
                  itemId: details.itemId ?? details.ItemId,
                  itemInstanceId:
                    details.itemInstanceId ?? details.ItemInstanceId ?? '',
                  quantity: Number(details.quantity ?? details.Quantity) || 1,
                });
              }
              break;
            }
            case 'DealDmg': {
              appendMessage(formatCombatLogEntry(combatEvent));
              break;
            }
            case 'RegenProgress': {
              const healed = Number(details.healed ?? details.Healed);
              if (Number.isFinite(healed) && healed > 0) {
                appendMessage(`Recovered ${healed} HP`);
              }
              break;
            }
            case UserEventTypes.HUNGER_PROGRESS: {
              const starvationDamage = Number(
                details.starvationDamage
                ?? details.StarvationDamage,
              );
              if (Number.isFinite(starvationDamage)
                && starvationDamage > 0) {
                appendMessage(
                  `Starvation cost ${Number(starvationDamage.toFixed(2))} HP.`,
                );
              }
              break;
            }
            case 'XpChange': {
              appendMessage(`Earned ${details.xp ?? details.Xp} XP`);
              if (
                activeCombatRewardIdRef.current
                && typeof onCombatRewardEvent === 'function'
              ) {
                onCombatRewardEvent({
                  kind: 'xp',
                  rewardId: activeCombatRewardIdRef.current,
                  amount: Number(details.xp ?? details.Xp) || 0,
                });
              }
              break;
            }
            case 'UpdateUserAction': {
              const action = details.userAction ?? details.UserAction;
              const normalizedAction = String(action || '').toUpperCase();
              if (
                action !== 'SCAVENGING'
                || lastLoggedActionRef.current !== action
              ) {
                appendMessage(`Now ${String(action).toLowerCase()}.`);
              }
              lastLoggedActionRef.current = action;
              if (
                activeCombatRewardIdRef.current
                && !['FIGHTING', 'FLEEING'].includes(normalizedAction)
              ) {
                const rewardId = activeCombatRewardIdRef.current;
                activeCombatRewardIdRef.current = null;
                onCombatRewardEvent?.({
                  kind: 'complete',
                  rewardId,
                });
              }
              break;
            }
            case 'IdleProgressPaused': {
              const allowedMsRaw = details.allowedMs ?? details.AllowedMs;
              const allowedMs = allowedMsRaw !== undefined ? Number(allowedMsRaw) : Number.NaN;
              let messageText = 'Idle progression paused.';
              if (Number.isFinite(allowedMs) && allowedMs > 0) {
                let allowedMinutes = allowedMs / 60000;
                if (allowedMinutes >= 10) {
                  allowedMinutes = Math.round(allowedMinutes);
                } else {
                  allowedMinutes = Math.round(allowedMinutes * 10) / 10;
                }
                messageText = `Idle progression paused — offline cap of ${allowedMinutes} minute${allowedMinutes !== 1 ? 's' : ''} reached.`;
              }
              appendMessage(messageText);
              break;
            }
            case 'FleeLog': {
              const msg = details.message ?? details.Message;
              if (msg) appendMessage(msg);
              break;
            }
            case 'CombatOutcome': {
              const outcomeMessage = formatCombatOutcome(details);
              appendMessage(outcomeMessage);
              const outcome = String(
                details.outcome ?? details.Outcome ?? '',
              ).toLowerCase();
              if (outcome === 'fled' && outcomeMessage) {
                ToastService.push(outcomeMessage, 'success');
              }
              if (outcome === 'victory') {
                const hasManifest = [
                  'xp',
                  'Xp',
                  'packLoot',
                  'PackLoot',
                  'groundLoot',
                  'GroundLoot',
                  'groundContainer',
                  'GroundContainer',
                ].some(key => Object.prototype.hasOwnProperty.call(
                  details,
                  key,
                ));
                const rewardId = String(
                  details.combatId
                  ?? details.CombatId
                  ?? eventId
                  ?? `victory-${receivedAtMs}`,
                );
                activeCombatRewardIdRef.current = hasManifest
                  ? null
                  : rewardId;
                onCombatRewardEvent?.({
                  kind: 'start',
                  rewardId,
                  enemyName:
                    details.enemyName ?? details.EnemyName ?? 'Unknown enemy',
                  manifestFinal: hasManifest,
                  xp: Number(details.xp ?? details.Xp) || 0,
                  packLoot: details.packLoot ?? details.PackLoot ?? [],
                  groundLoot:
                    details.groundLoot ?? details.GroundLoot ?? [],
                  groundContainer: normalizeGroundLootSummary(
                    details.groundContainer ?? details.GroundContainer,
                  ),
                });
              }
              break;
            }
            case UserEventTypes.RUN_RESET: {
              appendMessage(
                'Congratulations: you’re eligible for another run. Your Camp progress and Vault items survived; everything carried in your Pack did not. A complimentary Starter Cache awaits.',
              );
              refreshStarterCache().catch(error => console.error(
                'Failed to refresh the Starter Cache after a run reset',
                error,
              ));
              break;
            }
            case UserEventTypes.STARTER_LOADOUT_READY: {
              appendMessage('Starter loadout ready — health fully restored.');
              refreshStarterCache().catch(error => console.error(
                'Failed to refresh the Starter Cache after equipping starter gear',
                error,
              ));
              break;
            }
            case UserEventTypes.CONSUME_ITEM: {
              const itemId = details.itemId ?? details.ItemId;
              const item = itemId
                ? await GameAPI.fetchItemAuto(itemId, '')
                : null;
              const healed = Number(details.healed ?? details.Healed);
              const satietyRestored = Number(
                details.satietyRestored
                ?? details.SatietyRestored,
              );
              const currentHp = Number(
                details.currentHp ?? details.CurrentHp,
              );
              const hasTemporaryEffects = (
                item?.consumableEffects
                ?? item?.ConsumableEffects
                ?? []
              ).some(effect => [
                'TemporaryStatFlat',
                'TemporaryAttributeFlat',
              ].includes(effect?.type ?? effect?.Type));
              const effects = [];
              if (Number.isFinite(healed) && healed > 0) {
                effects.push(`recovered ${healed} HP`);
              }
              if (Number.isFinite(satietyRestored)
                && satietyRestored > 0
              ) {
                effects.push(
                  `restored ${Number(satietyRestored.toFixed(2))}% satiety`,
                );
              }
              if (hasTemporaryEffects) {
                effects.push('temporary effects queued');
              }
              const hpText = healed > 0 && Number.isFinite(currentHp)
                ? ` (${currentHp} HP now)`
                : '';
              const effectText = effects.join('; ')
                || 'applied its effects';
              appendMessage(
                `Used ${item?.name || 'consumable'} — ${effectText}${hpText}.`,
              );
              break;
            }
            case UserEventTypes.BASE_CAMP_CHANGED: {
              Promise.allSettled([
                refreshBaseCamp(),
                refreshUser(),
              ]).then(results => {
                results.forEach(result => {
                  if (result.status === 'rejected') {
                    console.error(
                      'Failed to refresh Camp state after an update',
                      result.reason,
                    );
                  }
                });
              });
              break;
            }
            case UserEventTypes.WORLD_INSTANCE_CHANGED: {
              refreshBaseCamp().catch(error => console.error(
                'Failed to refresh Camp state after changing world',
                error,
              ));
              break;
            }
            case UserEventTypes.REGION_TRANSITIONED: {
              appendMessage('Travelled to a connected region.');
              refreshBaseCamp().catch(error => console.error(
                'Failed to refresh Camp state after region travel',
                error,
              ));
              break;
            }
            case UserEventTypes.RESOURCE_COLLECTION_STARTED: {
              refreshResourceNodes().catch(error => console.error(
                'Failed to refresh reserved resource nodes',
                error,
              ));
              break;
            }
            case UserEventTypes.RESOURCE_COLLECTION_COMPLETED: {
              if (details.cancelled === true) {
                appendMessage('Gathering cancelled.');
              } else {
                const itemId = details.rewardItemId ?? details.RewardItemId;
                let itemName = 'resource';
                try {
                  itemName = (
                    await GameAPI.fetchItemAuto(itemId, '')
                  )?.name || itemName;
                } catch (error) {
                  console.error('Failed to resolve scavenged item', error);
                }
                appendMessage(
                  `Gathered ${
                    details.rewardQuantity ?? details.RewardQuantity ?? '?'
                  }× ${itemName}.`,
                );
              }
              refreshResourceNodes().catch(error => console.error(
                'Failed to refresh depleted resource nodes',
                error,
              ));
              break;
            }
            default:
              break;
          }
        } catch (err) {
          console.error('Failed to process event', err);
        }
      });

      if (typeof WebSocketService.subscribeConnection === 'function') {
        unsubscribeWsConnectionRef.current =
          WebSocketService.subscribeConnection(connection => {
            if (connection?.state === 'open' && connection.reconnected) {
              reconnectResyncRequiredRef.current = true;
              reconnectSnapshotRef.current = null;
              regionResyncSequenceRef.current += 1;
            }
          });
      }

      const token = window.localStorage.getItem('token');
      WebSocketService.start(token);
      setConnectionState({ status: 'ready', error: null });
    }

    bootstrap().catch(error => {
      if (!mounted) return;
      setConnectionState({
        status: 'error',
        error: error instanceof Error
          ? error
          : new Error('The game could not load its initial data.'),
      });
    });

    return () => {
      mounted = false;
      regionIdRef.current = null;
      regionWorldEpochRef.current += 1;
      groundLootRequestSequenceRef.current += 1;
      resourceNodeRequestSequenceRef.current += 1;
      regionResyncSequenceRef.current += 1;
      serverTimeOffsetMsRef.current = 0;
      worldInstanceIdRef.current = 'shared';
      reconnectResyncRequiredRef.current = false;
      reconnectSnapshotRef.current = null;
      activeCombatRewardIdRef.current = null;
      baseCampSnapshotRef.current = null;
      baseCampApplyGenerationRef.current = 0;
      if (reconnectRetryTimerRef.current) {
        window.clearTimeout(reconnectRetryTimerRef.current);
        reconnectRetryTimerRef.current = null;
      }
      bufferedInventoryEvents.length = 0;
      bufferedUserEvents.length = 0;
      if (unsubscribeStoreRef.current) unsubscribeStoreRef.current();
      if (unsubscribeWsRef.current) unsubscribeWsRef.current();
      if (unsubscribeWsConnectionRef.current) {
        unsubscribeWsConnectionRef.current();
      }
      WebSocketService.stop();
    };
  }, [
    appendMessage,
    bootstrapAttempt,
    onCombatEvent,
    onCombatRewardEvent,
    refreshGroundLoot,
    refreshResourceNodes,
    refreshBaseCamp,
    refreshStarterCache,
    refreshUser,
    reconcileBaseCampSnapshot,
    resyncAuthoritativeRegion,
  ]);

  return {
    user,
    region,
    regionWorldState,
    starterCache,
    baseCamp,
    baseCampLoading,
    baseCampError,
    resourceNodesLoading,
    resourceNodesError,
    messages,
    connectionStatus: connectionState.status,
    connectionError: connectionState.error,
    retryConnection,
    refreshGroundLoot,
    refreshResourceNodes,
    refreshBaseCamp,
    refreshStarterCache,
    refreshUser,
    reconcileInventory,
    reconcileBaseCampSnapshot,
    reconcileUserSnapshot,
  };
}
